// FormBuilderLibrary — the builder's left "Field library" sidebar: search over
// the typed field catalog, grouped into Basic / Capture / Advanced / Layout
// sections. Double-clicking an item calls onAdd(type) (append); items are also
// draggable — the drag carries the type under FIELD_DRAG_MIME and the canvas
// owns the drop position.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, Input } from '@/components/common';
import {
  FIELD_CATEGORIES,
  FIELD_DRAG_MIME,
  FIELD_LIBRARY,
  SECTION_DRAG_MIME,
  type FieldCategory,
} from './utils/fieldLibrary';
import type { LibraryElementType } from './utils/formElementDefaults';

interface FormBuilderLibraryProps {
  /** Called with the field type when an item is double-clicked. */
  onAdd: (type: LibraryElementType) => void;
  /** Types left out of the catalog — e.g. companyStructure once the form holds one. */
  hiddenTypes?: LibraryElementType[];
}

const FormBuilderLibrary = ({ onAdd, hiddenTypes }: FormBuilderLibraryProps) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  // Filter on the translated label so search works in both locales.
  const q = search.trim().toLowerCase();
  const visible = FIELD_LIBRARY.filter(
    (f) => !hiddenTypes?.includes(f.type) && (!q || t(f.labelKey).toLowerCase().includes(q)),
  );
  const byCategory = (cat: FieldCategory) => visible.filter((f) => f.category === cat);

  return (
    <aside className='form-builder-library' aria-label={t('forms_builder_library_title')}>
      <h2 className='form-builder-library__title'>{t('forms_builder_library_title')}</h2>
      <p className='form-builder-library__hint'>{t('forms_builder_library_hint')}</p>

      <Input
        size='sm'
        iconStart='search'
        placeholder={t('forms_builder_library_search_ph')}
        aria-label={t('forms_builder_library_search_ph')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <div className='form-builder-library__sections'>
        {FIELD_CATEGORIES.map((cat) => {
          const items = byCategory(cat);
          if (!items.length) return null;
          return (
            <section key={cat} className='form-builder-library__section'>
              <h3
                className={`form-builder-library__section-head form-builder-library__section-head--${cat}`}
              >
                {t(`forms_builder_cat_${cat}`)}
              </h3>
              {items.map((f) => (
                <button
                  key={f.type}
                  type='button'
                  className='form-builder-library__item'
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData(FIELD_DRAG_MIME, f.type);
                    // Flavor (not payload) — lets drop targets tell a section
                    // drag apart during dragover, which can only read `types`.
                    if (f.type === 'section') e.dataTransfer.setData(SECTION_DRAG_MIME, f.type);
                    // No text/plain flavor: nothing consumes it, and a missed
                    // drop would paste the raw type into any focused input.
                    e.dataTransfer.effectAllowed = 'copy';
                  }}
                  onDoubleClick={() => onAdd(f.type)}
                >
                  <span className='form-builder-library__item-icon'>
                    <Icon name={f.icon} size={14} />
                  </span>
                  {t(f.labelKey)}
                </button>
              ))}
            </section>
          );
        })}
        {!visible.length && (
          <p className='form-builder-library__empty'>{t('forms_builder_library_empty')}</p>
        )}
      </div>
    </aside>
  );
};

export default FormBuilderLibrary;
